// @ts-ignore
import axios from 'axios';

import { isDefined } from '../../utils/variables';
import { trimUrl } from '../web-api-client';

interface FileUploadOptions {
  apiBaseUrl: string;
  authToken: string;
  // @ts-ignore
  file: File;
  concurrency: number;
}

interface PartQueueItem {
  url: string;
  partNumber: number;
  startByte: number;
  bytesUploaded: number;
}

interface CompletedPartItem {
  partNumber: number;
  etag: string;
}

interface UploadData {
  id: string;
  partUrls: string[];
}

type ProgressListener = (bytesUploaded: number, totalBytes: number) => void;

export class UploadManuallyStoppedError extends Error {
  constructor() {
    super('Upload manually stopped.');
  }
}

export class FileUpload {
  private readonly partSize: number;
  private readonly partCount: number;

  private queue: PartQueueItem[] = [];
  private progressStatusItems: PartQueueItem[] = [];
  private completed: CompletedPartItem[] = [];

  private workers: Promise<void[]> = Promise.resolve([]);
  private stopped = false;
  private activeControllers = new Set<AbortController>();

  // Backend limit to ensure payload is maintained
  // 500 presigned url's are about 600 kb
  private multipartCountLimit = 500;

  // The limit for S3 multipart upload
  // https://docs.aws.amazon.com/AmazonS3/latest/dev/qfacts.html
  private s3MultipartMBSizeLimit = { min: 5, max: 5000 };
  private s3MBTotalSizeLimit = 5 * 1024 * 1024;

  private progressListeners: ProgressListener[] = [];

  constructor(private options: FileUploadOptions) {
    const { partSize, partsCount } = this.countParts(this.options.file.size);

    this.partSize = partSize;
    this.partCount = partsCount;
  }

  static isManuallyStoppedError(error: unknown) {
    return error instanceof UploadManuallyStoppedError;
  }

  reset() {
    this.stopped = false;
    this.queue = [];
    this.progressStatusItems = [];
    this.completed = [];
    this.activeControllers.clear();
  }

  async start(): Promise<{ id: string }> {
    this.reset();
    const data: UploadData = await this.initialize();

    this.queue.push(
      ...data.partUrls.map((url, index) => ({
        url,
        partNumber: index + 1,
        startByte: index * this.partSize,
        bytesUploaded: 0,
      })),
    );

    this.workers = Promise.all(
      Array.from(Array(this.options.concurrency), () => this.startWorker()),
    );
    await this.workers;

    if (this.stopped) {
      return Promise.reject(new UploadManuallyStoppedError());
    }

    return this.complete(data.id);
  }

  stop() {
    this.stopped = true;
    for (const controller of this.activeControllers) {
      controller.abort();
    }
  }

  onProgress(listener: ProgressListener) {
    this.progressListeners.push(listener);
  }

  private notifyProgressListeners() {
    // Sum total uploaded bytes for all parts.
    const bytesUploaded = this.progressStatusItems
      .map((item) => item.bytesUploaded)
      .reduce((a, b) => a + b, 0);
    const totalBytes = this.options.file.size;

    for (const listener of this.progressListeners) {
      listener(bytesUploaded, totalBytes);
    }
  }

  private checkResponseStatus(response: any) {
    if (response.status === 401) {
      throw new Error('Token has expired.');
    }
  }

  private async initialize() {
    // @ts-ignore
    const response = await fetch(trimUrl(`${this.options.apiBaseUrl}/v1/uploads`), {
      method: 'POST',
      // @ts-ignore
      headers: new Headers({
        Authorization: `Bearer ${this.options.authToken}`,
      }),
      body: JSON.stringify({
        name: this.options.file.name,
        partCount: this.partCount,
      }),
    });

    this.checkResponseStatus(response);

    return response.json();
  }

  private async startWorker() {
    while (this.queue.length > 0) {
      if (this.stopped) {
        return;
      }

      const item = this.queue.shift()!;
      this.progressStatusItems.push(item);

      const etag = await this.uploadPart(item);
      this.completed.push({
        partNumber: item.partNumber,
        etag,
      });
    }
  }

  private async awsPut(item: PartQueueItem): Promise<string> {
    const controller = new AbortController();
    this.activeControllers.add(controller);
    try {
      const response = await axios.put(
        item.url,
        this.options.file.slice(item.startByte, item.startByte + this.partSize),
        {
          signal: controller.signal,
          onUploadProgress: (evt) => {
            if (!this.stopped && isDefined(evt.total)) {
              item.bytesUploaded = evt.loaded;
              this.notifyProgressListeners();
            }
          },
        },
      );
      return response.headers.etag as string;
    } catch (e) {
      if (axios.isCancel(e) && this.stopped) {
        throw new UploadManuallyStoppedError();
      }
      throw e;
    } finally {
      this.activeControllers.delete(controller);
    }
  }

  private async uploadPart(item: PartQueueItem, retry = 0): Promise<string> {
    try {
      return await this.awsPut(item);
    } catch (e) {
      if (this.stopped) {
        throw e;
      }

      if (retry > 10) {
        this.stopped = true;
        throw e;
      }

      return await this.uploadPart(item, retry + 1);
    }
  }

  private async complete(id: string): Promise<{ id: string }> {
    // @ts-ignore
    const response = await fetch(trimUrl(`${this.options.apiBaseUrl}/v1/uploads/${id}`), {
      method: 'PATCH',
      // @ts-ignore
      headers: new Headers({
        Authorization: `Bearer ${this.options.authToken}`,
      }),
      body: JSON.stringify({ parts: this.completed }),
    });

    this.checkResponseStatus(response);

    return response.json();
  }

  private countParts(fileSizeInBytes: number) {
    const fileSizeInMB = fileSizeInBytes / 1024 / 1024;

    if (fileSizeInMB > this.s3MBTotalSizeLimit) {
      throw new Error('Failed to upload file. Upload file limit is 5 TB.');
    }

    // The formula is computed using polynomial regression given the below reference data points:
    // File size (MB) vs part size (MB)
    // 50 5
    // 100 10
    // 500 20
    // 1000 25
    // 5000 40
    // 10000 65
    // 15000 80
    // 25000 110
    // 30000 125
    // 40000 140
    // 50000 150
    // You can use http://www.xuru.org/rt/PR.asp#Manually or any other similar service with the updated data points to
    // retrieve a new formula.
    const partSizeInMB =
      0.0000000000005351500138 * Math.pow(fileSizeInMB, 3) -
      0.00000008924202181 * Math.pow(fileSizeInMB, 2) +
      0.005882472674 * fileSizeInMB +
      12.53018644;

    // Ensures that part size can not be less than 5 MB nor bigger than 5 GB
    // (https://docs.aws.amazon.com/AmazonS3/latest/dev/qfacts.html).
    const partSizeInBytes =
      Math.min(
        Math.max(this.s3MultipartMBSizeLimit.min, Math.round(partSizeInMB)),
        this.s3MultipartMBSizeLimit.max,
      ) *
      1024 *
      1024;

    const partsCount = Math.ceil(fileSizeInBytes / partSizeInBytes);

    // Ensures that parts count can not be more than 50 as uploads service allows maximum of 50 parts. In this case
    // we reverse the formula and compute part size for the 50 parts.
    return partsCount <= this.multipartCountLimit
      ? { partSize: partSizeInBytes, partsCount }
      : {
          partsCount: this.multipartCountLimit,
          partSize: Math.ceil(fileSizeInBytes / this.multipartCountLimit),
        };
  }
}
